import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useIsMobile } from "@/hooks/use-mobile";

// Import refactored components
import EventHeader from "@/components/event-registration/EventHeader";
import RegistrationSteps from "@/components/event-registration/RegistrationSteps";
import RegistrationForm from "@/components/event-registration/RegistrationForm";
import PaymentForm from "@/components/event-registration/PaymentForm";
import ConfirmationScreen from "@/components/event-registration/ConfirmationScreen";
import OrderSummary from "@/components/event-registration/OrderSummary";
import LoadingScreen from "@/components/event-registration/LoadingScreen";
import MobileOrderSummary from "@/components/event-registration/MobileOrderSummary";

// Import refactored context and hooks
import {
  RegistrationProvider,
  useRegistration,
  registrationSchema,
  RegistrationFormValues,
} from "@/components/event-registration/RegistrationContext";
import { Ticket, useEventData } from "@/hooks/use-event-data";
import { TicketPurchasePayload } from "@/api/events/models/EventInput";
import { EventsService } from "@/api/events";
import StripesPayment from "./StripesPayment";

const eventsData = {
  "1": {
    id: "1",
    title: "Tech Innovation Summit 2023",
    description:
      "Join industry leaders for discussions on emerging technologies and digital transformation strategies.",
    date: "2023-12-15",
    startTime: "09:00",
    endTime: "17:00",
    location: "San Francisco Convention Center",
    address: "747 Howard St, San Francisco, CA 94103",
    image:
      "https://images.unsplash.com/photo-1540575467063-178a50c2df87?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80",
    ticketTypes: [
      {
        id: "standard",
        name: "Standard Ticket",
        price: 299,
        description: "Full event access for one attendee",
      },
      {
        id: "vip",
        name: "VIP Ticket",
        price: 499,
        description: "Premium access with VIP seating and exclusive networking",
      },
      {
        id: "early",
        name: "Early Bird",
        price: 199,
        description: "Limited availability, first come first served",
      },
    ],
    customFields: [
      { id: "company", label: "Company Name", type: "text", required: true },
      { id: "jobTitle", label: "Job Title", type: "text", required: true },
      {
        id: "dietary",
        label: "Dietary Restrictions",
        type: "text",
        required: false,
      },
      {
        id: "questions",
        label: "Questions for Speakers",
        type: "textarea",
        required: false,
      },
    ],
  },
  "2": {
    id: "2",
    title: "Digital Marketing Masterclass",
    description:
      "Learn the latest digital marketing strategies to grow your business online.",
    date: "2023-11-25",
    startTime: "10:00",
    endTime: "15:00",
    location: "Virtual Event",
    address: "Online",
    image:
      "https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80",
    ticketTypes: [
      {
        id: "standard",
        name: "Standard Access",
        price: 149,
        description: "Full event access",
      },
      {
        id: "premium",
        name: "Premium Access",
        price: 249,
        description: "Full access plus 1-on-1 consultation",
      },
    ],
    customFields: [
      { id: "company", label: "Company Name", type: "text", required: true },
      { id: "jobTitle", label: "Job Title", type: "text", required: true },
      {
        id: "experience",
        label: "Marketing Experience Level",
        type: "text",
        required: true,
      },
    ],
  },
  "3": {
    id: "3",
    title: "Healthcare Innovation Conference",
    description:
      "Exploring the future of healthcare technology and patient care models.",
    date: "2024-01-10",
    startTime: "08:30",
    endTime: "16:30",
    location: "Boston Medical Center",
    address: "750 Albany St, Boston, MA 02119",
    image:
      "https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80",
    ticketTypes: [
      {
        id: "professional",
        name: "Healthcare Professional",
        price: 249,
        description: "For doctors, nurses, and healthcare staff",
      },
      {
        id: "industry",
        name: "Industry Partner",
        price: 349,
        description: "For healthcare technology companies",
      },
      {
        id: "student",
        name: "Student/Resident",
        price: 99,
        description: "Discounted rate for students and residents",
      },
    ],
    customFields: [
      {
        id: "institution",
        label: "Institution/Hospital",
        type: "text",
        required: true,
      },
      { id: "role", label: "Professional Role", type: "text", required: true },
      {
        id: "specialization",
        label: "Specialization",
        type: "text",
        required: false,
      },
    ],
  },
  "4": {
    id: "4",
    title: "Sustainable Business Forum",
    description:
      "Strategies for implementing sustainable practices in your business operations.",
    date: "2024-02-05",
    startTime: "09:00",
    endTime: "16:00",
    location: "Seattle Convention Center",
    address: "705 Pike St, Seattle, WA 98101",
    image:
      "https://images.unsplash.com/photo-1464692805480-a69dfaafdb0d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80",
    ticketTypes: [
      {
        id: "standard",
        name: "Standard Ticket",
        price: 179,
        description: "Full access to all forum sessions",
      },
      {
        id: "exhibitor",
        name: "Exhibitor Package",
        price: 499,
        description: "Booth space and 2 attendee passes",
      },
    ],
    customFields: [
      { id: "company", label: "Company Name", type: "text", required: true },
      { id: "position", label: "Position", type: "text", required: true },
      {
        id: "interests",
        label: "Sustainability Interests",
        type: "textarea",
        required: false,
      },
    ],
  },
  "5": {
    id: "5",
    title: "AI in Education Summit",
    description:
      "Exploring the applications of artificial intelligence in modern education.",
    date: "2023-12-03",
    startTime: "10:00",
    endTime: "15:00",
    location: "Chicago Educational Center",
    address: "425 S Wabash Ave, Chicago, IL 60605",
    image:
      "https://images.unsplash.com/photo-1517048676732-d65bc937f952?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80",
    ticketTypes: [
      {
        id: "educator",
        name: "Educator Pass",
        price: 129,
        description: "For teachers and educational staff",
      },
      {
        id: "administrator",
        name: "Administrator Pass",
        price: 179,
        description: "For school and district administrators",
      },
      {
        id: "vendor",
        name: "EdTech Vendor",
        price: 249,
        description: "For educational technology companies",
      },
    ],
    customFields: [
      {
        id: "institution",
        label: "School/Institution",
        type: "text",
        required: true,
      },
      { id: "role", label: "Role", type: "text", required: true },
      {
        id: "aiExperience",
        label: "AI Experience Level",
        type: "text",
        required: false,
      },
    ],
  },
  "6": {
    id: "6",
    title: "Global Finance Conference",
    description:
      "Insights into global financial trends and investment strategies for 2024.",
    date: "2024-01-20",
    startTime: "08:00",
    endTime: "18:00",
    location: "New York Financial Center",
    address: "1335 Avenue of the Americas, New York, NY 10019",
    image:
      "https://images.unsplash.com/photo-1551836022-d5d88e9218df?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80",
    ticketTypes: [
      {
        id: "professional",
        name: "Financial Professional",
        price: 349,
        description: "For finance industry professionals",
      },
      {
        id: "executive",
        name: "Executive Pass",
        price: 649,
        description: "For C-suite and senior executives",
      },
      {
        id: "academic",
        name: "Academic/Research",
        price: 199,
        description: "For academics and researchers",
      },
    ],
    customFields: [
      {
        id: "organization",
        label: "Organization",
        type: "text",
        required: true,
      },
      { id: "title", label: "Title/Position", type: "text", required: true },
      {
        id: "interests",
        label: "Financial Interests",
        type: "textarea",
        required: false,
      },
    ],
  },
};

const customFields = [
  { id: "company", label: "Company Name", type: "text", required: true },
  { id: "jobTitle", label: "Job Title", type: "text", required: true },
  {
    id: "dietary",
    label: "Dietary Restrictions",
    type: "text",
    required: false,
  },
  {
    id: "questions",
    label: "Questions for Speakers",
    type: "textarea",
    required: false,
  },
];

const EventRegistrationContent = () => {
  const { eventId } = useParams();
  const { toast } = useToast();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { registerPaidEvent } = EventsService;
  const [paymentSecret, setPaymentSecret] = useState("");
  const [paymentScreen, setPaymentScreen] = useState(false);
  const [loader, setLoader] = useState(false);
  const [emails, setEmails] = useState<string[]>([]);

  // Get event data from custom hook
  const { eventData, loading } = useEventData(eventId);

  // Get registration context
  const {
    step,
    setStep,
    isPaymentProcessing,
    setIsPaymentProcessing,
    showOrderSummary,
    setForm,
  } = useRegistration();

  // Form handling
  const form = useForm<RegistrationFormValues>({
    resolver: zodResolver(registrationSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: [""],
      phone: "",
      ticketType: "",
      quantity: 1,
      // company: "",
      // jobTitle: "",
      // dietary: "",
      // questions: "",
      marketingConsent: false,
      agreeTerms: false,
    },
  });

  // Store form in context
  useEffect(() => {
    setForm(form);
  }, [form, setForm]);

  // Update form default ticket type when event data loads
  useEffect(() => {
    if (
      // eventData &&
      eventData &&
      eventData?.tickets?.length > 0
    ) {
      form.setValue("ticketType", eventData?.tickets[0]?.ticket_name);
    }
  }, [eventData, form]);

  const selectedTicketType = eventData?.tickets?.find(
    (ticket: Ticket) => ticket.ticket_name === form.watch("ticketType")
  );

  const quantity = form.watch("quantity");
  const totalAmount = selectedTicketType
    ? Number(selectedTicketType.price) * quantity
    : Number(eventData?.ticket_price) * quantity;

  const onSubmit = async (data: RegistrationFormValues) => {
    if (step === 1) {
      try {
        setIsPaymentProcessing(true);
        setLoader(true);

        const isPaidEvent = Number(eventData?.ticket_price) > 0;

        const isTicket = eventData?.tickets && eventData?.tickets.length > 0;

        let payload: TicketPurchasePayload;

        if (isPaidEvent || isTicket) {
          payload = {
            event_id: eventData.event_id,
            user_id: eventData.pk,
            first_name: data.firstName,
            last_name: data.lastName,
            email: data.email,
            // location: eventData.location,
            // meeting_link: eventData.meeting_link,

            ...(eventData.location && { location: eventData.location }),
            ...(eventData.meeting_link && {
              meeting_link: eventData.meeting_link,
            }),
            ...(selectedTicketType?.ticket_name && {
              ticket_type: selectedTicketType.ticket_name,
            }),

            phone_number: data.phone,
            payment_method_id: "pm_card_visa",
            // ticket_type: selectedTicketType?.ticket_name,
            ticket_price:
              Number(selectedTicketType?.price) ||
              Number(eventData?.ticket_price),
            quantity: quantity,
            // company_name: data.company,
            // job_title: data.jobTitle,
            // dietary_restrictions: data.dietary,
            // questions_for_speakers: data.questions,
          };
        } else {
          payload = {
            event_id: eventData.event_id,
            user_id: eventData.pk,
            first_name: data.firstName,
            last_name: data.lastName,
            // email: data.email,

            ...(eventData.location && { location: eventData.location }),
            ...(eventData.meeting_link && {
              meeting_link: eventData.meeting_link,
            }),
            email: data.email,

            phone_number: data.phone,

            quantity: quantity,
            // company_name: data.company,
            // job_title: data.jobTitle,
            // dietary_restrictions: data.dietary,
            // questions_for_speakers: data.questions,
          };
        }

        const response: any = await registerPaidEvent(payload);
        if (response.statusCode === 200) {
          setLoader(false);

          toast({
            title: "Registration Successful!",
            description: response.body.message,
          });
          if (!isPaidEvent) {
            // toast({
            //   title: "Registration Successful!",
            //   description: response.body.message,
            // });

            // setSuccess(true);
            setStep(3);

            // Optionally scroll to top
            if (isMobile) {
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          } else {
            // Just show success for paid event

            setStep(2);
            setIsPaymentProcessing(false);
            setPaymentSecret(response.body.client_secret);
            // On mobile, automatically scroll to top when advancing to payment
            if (isMobile) {
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }

          // setLoader(false);
          // setStep(2);
          // // // On mobile, automatically scroll to top when advancing to payment
          // if (isMobile) {
          //   window.scrollTo({ top: 0, behavior: "smooth" });
          // }

          // setIsPaymentProcessing(false);
          // setPaymentSecret(response.body.client_secret);
          // // setSuccess(true);
          // // setStep(3);
          // // toast({
          // //   title: "Registration Successful!",
          // //   description: response.body.message,
          // // });
          // // On mobile, automatically scroll to top for confirmation
          // if (isMobile) {
          //   window.scrollTo({ top: 0, behavior: "smooth" });
          // }
        } else {
          toast({
            title: "Error",
            description: response.body.message || "Something went wrong",
          });
          setLoader(false);
        }
      } catch (error) {
        setLoader(false);

        setIsPaymentProcessing(false);
      }
    } else if (step === 2) {
      setStep(3);
      // setIsPaymentProcessing(true);
      // setStep(3);

      // Simulate payment processing
      // setTimeout(() => {
      //   setIsPaymentProcessing(false);

      //   // Show success message
      //   toast({
      //     title: "Registration Successful!",
      //     description: "Your ticket confirmation has been sent to your email.",
      //   });

      //   setStep(3);

      //   // On mobile, automatically scroll to top for confirmation
      if (isMobile) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      // }, 2000);
    }
  };

  const handleContinueToPayment = () => {
    form.handleSubmit(onSubmit)();
  };

  const handleCompleteRegistration = () => {
    onSubmit(form.getValues());
  };

  const handleRegisterAnother = () => {
    form.reset();
    setStep(1);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <main className="flex-grow bg-gray-50 pb-12">
      {/* Event Header */}
      <EventHeader
        title={eventData.event_title}
        date={eventData.event_date}
        location={eventData?.location}
        virtualEvent={eventData.virtual_event}
        image={eventData.img_url[0]}
      />

      <div className="max-w-5xl mx-auto px-4 py-6 md:py-8">
        {/* Registration Steps */}
        <RegistrationSteps currentStep={step} />

        {/* Mobile Order Summary Toggle */}
        {isMobile && (
          <MobileOrderSummary
            selectedTicketType={selectedTicketType}
            quantity={quantity}
            eventPrice={eventData.ticket_price}
            eventDate={eventData.event_date}
            eventStartTime={eventData.start_time}
            eventEndTime={eventData.end_time}
            eventLocation={eventData?.location}
          />
        )}

        {/* Registration Form Container */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
          {/* Main Form */}
          <div className="md:col-span-2">
            <Card>
              <CardContent className="pt-6">
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="space-y-6"
                  >
                    {step === 1 && (
                      <RegistrationForm
                        form={form}
                        ticketTypes={eventData?.tickets}
                        // customFields={customFields}
                        loader={loader}
                        emails={emails}
                        setEmails={setEmails}

                        // onContinue={handleContinueToPayment}
                      />
                    )}

                    {step === 3 && (
                      <ConfirmationScreen
                        eventTitle={eventData.event_title}
                        eventDate={eventData.event_date}
                        selectedTicketType={selectedTicketType}
                        eventPrice={eventData.ticket_price}
                        quantity={quantity}
                        totalAmount={totalAmount}
                        onRegisterAnother={handleRegisterAnother}
                      />
                    )}
                  </form>
                </Form>

                {step === 2 && (
                  <StripesPayment
                    publicSecrete={paymentSecret}
                    handleSucess={() => setStep(3)}
                    setPaymentScreen={setPaymentScreen}
                  />
                  // <PaymentForm
                  //   isProcessing={isPaymentProcessing}
                  //   onBack={() => setStep(1)}
                  //   onSubmit={handleCompleteRegistration}
                  // />
                )}
              </CardContent>
            </Card>
          </div>

          {/* Desktop Order Summary */}
          {!isMobile && (
            <div className="md:col-span-1 hidden md:block">
              <OrderSummary
                selectedTicketType={selectedTicketType}
                quantity={quantity}
                eventDate={eventData.event_date}
                eventStartTime={eventData.start_time}
                eventEndTime={eventData.end_time}
                virtualEvent={eventData.virtual_event}
                eventLocation={eventData?.location}
                eventPrice={eventData.ticket_price}
              />
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

const EventRegistration = () => {
  const isMobile = useIsMobile();

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <RegistrationProvider initialShowOrderSummary={!isMobile}>
        <EventRegistrationContent />
      </RegistrationProvider>
      <Footer />
    </div>
  );
};

export default EventRegistration;
